import React, { useEffect, useState } from 'react';
import { Button, TextField, Typography } from '@material-ui/core';

import { warehouseApi } from 'api/warehouse';
import { FormComponent, Notifications } from 'shared';

function ChangeExpirationDateAction({ expLifeDays = null, lotId, onCancel, onSuccess }) {
    const [notification, setNotification] = useState({ text: null, level: null });
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [currentFormData, setCurrentFormData] = useState({});
    const [expiresManuallyOverridden, setExpiresManuallyOverridden] = useState(false);
    const [confirmExpiresOn, setConfirmExpiresOn] = useState(null);
    const showProductionDateFields = expLifeDays !== null;
    const formFields = [];

    useEffect(() => {
        if (!(onSuccess instanceof Function)) {
            console.error('ChangeExpirationDateAction intialized incorrectly onSuccess is not a function');
        }
        if (!(onCancel instanceof Function)) {
            console.error('ChangeExpirationDateAction initialized incorrectly onCancel is not a function');
        }
    }, [onSuccess, onCancel]);

    const calculateExpirationDateString = (productionDate) => {
        if (!productionDate || expLifeDays === null || isNaN(Number(expLifeDays))) {
            return '';
        }
        // Use UTC math to avoid timezone off-by-one errors
        const [y, m, d] = productionDate.split('-').map(Number);
        const utcDate = new Date(Date.UTC(y, m - 1, d));
        utcDate.setUTCDate(utcDate.getUTCDate() + Number(expLifeDays));
        return utcDate.toISOString().slice(0, 10);
    };

    const handleProductionDateChange = (event, value) => {
        // When production date changes, re-enable auto-calc unless user edits expiresOn after
        setExpiresManuallyOverridden(false);
    };

    if (showProductionDateFields) {
        formFields.push(
            {
                name: 'productionDate',
                label: 'Production Date',
                inputElement: 'date',
                gridValue: 4,
                inputProps: { onChange: handleProductionDateChange },
            },
            {
                name: 'expiresOn',
                label: 'Expires On',
                gridValue: 4,
                // Custom input so we can update form value when production date changes,
                // but still allow manual override
                inputElement: ({ formData, setFormField }) => {
                    function AutoSync() {
                        useEffect(() => {
                            if (!expiresManuallyOverridden && formData.productionDate) {
                                const computed = calculateExpirationDateString(formData.productionDate);
                                if ((computed || '') !== (formData.expiresOn || '')) {
                                    setFormField(computed);
                                }
                            }
                        }, [formData.productionDate, expiresManuallyOverridden]);
                        return null;
                    }

                    return (
                        <>
                            <AutoSync />
                            <TextField
                                id="ExpiresOn_dateField"
                                type="date"
                                label="Expires On"
                                value={formData.expiresOn || ''}
                                onClick={(event) => {
                                    if (event.target && typeof event.target.showPicker === 'function') {
                                        event.target.showPicker();
                                    }
                                }}
                                onChange={(event) => {
                                    setExpiresManuallyOverridden(true);
                                    setFormField(event.target.value);
                                }}
                                InputLabelProps={{ shrink: true }}
                                required
                            />
                        </>
                    );
                },
                inputProps: { required: true },
            },
        );
    } else {
        formFields.push({
            name: 'expiresOn',
            label: 'Expires On',
            inputElement: 'date',
            gridValue: 4,
            inputProps: { required: true },
        });
    }

    const handleSubmit = async (formData, resetForm) => {
        // Only use the value in the expiresOn field for submission
        const resolvedExpiresOn = formData.expiresOn;

        const expiresOnDate = new Date(resolvedExpiresOn);
        const today = new Date();
        // Compare dates at day precision
        expiresOnDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        if (expiresOnDate < today) {
            setConfirmExpiresOn(resolvedExpiresOn);
            setShowConfirmation(true);
            return;
        }
        await handleConfirm(resolvedExpiresOn);
    };

    const handleConfirm = async (expiresOnParam = null) => {
        const expiresOn = expiresOnParam ?? currentFormData.expiresOn;
        const result = await warehouseApi.updateLot(lotId, {
            expiresOn,
        });
        if (!result.status) {
            setNotification({ text: result.msg, level: 'error' });
        } else if (onSuccess instanceof Function) {
            onSuccess();
        }
    };

    const handleCancel = () => {
        setShowConfirmation(false);
        onCancel();
    };

    const handleFormDataChange = (formData) => {
        setCurrentFormData(formData);
    };

    return (
        <>
            <Notifications options={notification} />
            <h3>Change Expiration Date</h3>
            {showProductionDateFields && (
                <Typography variant="body2" style={{ margin: '8px 0 12px', color: '#555' }}>
                    This item is expected to expire {expLifeDays} day{Number(expLifeDays) === 1 ? '' : 's'} after
                    production. Select a production date to auto-calculate the expiration date, or adjust Expires On
                    manually.
                </Typography>
            )}
            <FormComponent
                formFields={formFields}
                onSubmit={handleSubmit}
                button={{ text: 'Submit', gridValue: 1 }}
                onFormDataChange={handleFormDataChange}
            />
            {showConfirmation && (
                <>
                    <Typography style={{ color: 'red', marginBottom: 10 }}>
                        You are trying to update the Expiration Date to a date that has already passed
                    </Typography>
                    <Button
                        onClick={() => handleConfirm(confirmExpiresOn || currentFormData.expiresOn)}
                        color="primary"
                        variant="outlined"
                    >
                        Confirm
                    </Button>
                    <Button onClick={handleCancel} variant="outlined" style={{ color: 'red', marginLeft: 10 }}>
                        Cancel
                    </Button>
                </>
            )}
        </>
    );
}

export { ChangeExpirationDateAction };
